<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import {
  TruckIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
} from "@heroicons/vue/24/outline";
import { type DashboardData } from "@/models";
import services from "@/services";
import { useUserDataStore } from "@/stores/userDataStore";
import { useToaster } from "ram-components";

const router = useRouter();
const userDataStore = useUserDataStore();
const toaster = useToaster();
const showLoading = ref(false);

const dashboardData = ref<DashboardData | null>(null);
const lastUpdated = ref(new Date());
const timeAgo = ref("Just now");
let pollingInterval: ReturnType<typeof setInterval> | null = null;
let timeAgoInterval: ReturnType<typeof setInterval> | null = null;

onMounted(async () => {
  await fetchDashboardDetails();

  pollingInterval = setInterval(fetchDashboardDetails, 300000);

  timeAgoInterval = setInterval(updateTimeLabel, 60000);
});

onUnmounted(() => {
  if (pollingInterval) clearInterval(pollingInterval);
  if (timeAgoInterval) clearInterval(timeAgoInterval);
});

function updateTimeLabel() {
  const diffInMinutes = Math.floor(
    (new Date().getTime() - lastUpdated.value.getTime()) / 60000,
  );
  timeAgo.value = diffInMinutes < 1 ? "Just now" : `${diffInMinutes} min ago`;
}

async function fetchDashboardDetails() {
  try {
    showLoading.value = true;
    const response = await services.getDashboardDetails();
    dashboardData.value = response.data.result;

    lastUpdated.value = new Date();
    updateTimeLabel();

    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = setInterval(fetchDashboardDetails, 300000);
    }
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to load dashboard data.",
    });
  } finally {
    showLoading.value = false;
  }
}
</script>

<template>
  <div
    v-if="showLoading"
    class="flex h-screen flex-col items-center justify-center"
  >
    <div
      class="h-10 w-10 animate-spin rounded-full border-8 border-ram-red border-t-transparent"
    ></div>
  </div>
  <div class="m-2" v-else>
    <div class="mb-8">
      <h1 class="text-2xl font-bold text-gray-900">Dashboard</h1>

      <div
        class="mt-1 flex flex-col gap-2 text-gray-500 md:flex-row md:items-center md:justify-between"
      >
        <p>Welcome back! Here's an overview of your parcel activity.</p>

        <div
          class="flex items-center gap-2 self-start text-sm text-ram-red md:self-auto"
        >
          <span>{{ timeAgo }}</span>
          <button
            @click="fetchDashboardDetails"
            class="rounded-full p-1 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-300"
            title="Refresh Data"
          >
            <ArrowPathIcon class="h-4 w-4 text-red-600" />
          </button>
        </div>
      </div>
    </div>

    <div class="grid gap-1 md:grid-cols-2 lg:grid-cols-2">
      <div
        class="cursor-pointer rounded-xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md"
      >
        <div class="flex items-start gap-5">
          <div
            class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-ram-red"
          >
            <TruckIcon class="h-6 w-6 text-white" />
          </div>
          <div>
            <p class="font-medium text-ram-red">Parcels Expected</p>
            <p class="mt-1 text-3xl font-bold text-ram-red">
              {{ dashboardData?.parcelExpected }}
            </p>
          </div>
        </div>
      </div>
      <div
        class="cursor-pointer rounded-xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md"
      >
        <div class="flex items-start gap-5">
          <div
            class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-600"
          >
            <ArchiveBoxIcon class="h-6 w-6 text-white" />
          </div>
          <div>
            <p class="font-medium text-red-600">Parcels In Hub</p>
            <p class="mt-1 text-3xl font-bold text-red-600">
              {{ dashboardData?.parcelInHub }}
            </p>
          </div>
        </div>
      </div>
      <div
        class="cursor-pointer rounded-xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md"
      >
        <div class="flex items-start gap-5">
          <div
            class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-600"
          >
            <ArchiveBoxIcon class="h-6 w-6 text-white" />
          </div>
          <div>
            <p class="font-medium text-red-600">Collected Today</p>
            <p class="mt-1 text-3xl font-bold text-red-600">
              {{ dashboardData?.collectedToday }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
